import { motion } from "framer-motion";
import { useEffect, useMemo, useRef, useState } from "react";
import { classNames } from "@/react/helpers/twMergeWithCN";
import { t } from "@/i18n-js/instance";

const i18nRoot = "eoy_recap_2025.sections.impact.bento_items.months";

interface MonthsDisplayProps {
  month: {
    amount: number;
    month: string;
  };
}

const getAllMonths = () => [
  t([i18nRoot, "january"]),
  t([i18nRoot, "february"]),
  t([i18nRoot, "march"]),
  t([i18nRoot, "april"]),
  t([i18nRoot, "may"]),
  t([i18nRoot, "june"]),
  t([i18nRoot, "july"]),
  t([i18nRoot, "august"]),
  t([i18nRoot, "september"]),
  t([i18nRoot, "october"]),
  t([i18nRoot, "november"]),
  t([i18nRoot, "december"]),
];

const getCyclicMonths = (activeMonth: number) => {
  const allMonths = getAllMonths();
  const months: Array<{ name: string; number: number }> = [];
  const activeIndex = activeMonth - 1;

  for (let i = 0; i < 12; i++) {
    const monthIndex = (activeIndex - 6 + i + 12) % 12;
    const monthNumber = monthIndex + 1;
    months.push({
      name: allMonths[monthIndex],
      number: monthNumber,
    });
  }

  return months;
};

export const MonthsDisplay = ({ month }: MonthsDisplayProps) => {
  const monthNumber = useMemo(() => {
    const monthRegex = /-(\d{2})$/;
    const monthMatch = monthRegex.exec(month.month);
    return monthMatch ? parseInt(monthMatch[1], 10) : 1;
  }, [month.month]);

  const cyclicMonths = useMemo(
    () => getCyclicMonths(monthNumber),
    [monthNumber],
  );
  const containerRef = useRef<HTMLDivElement>(null);
  const monthRefs = useRef<Array<HTMLDivElement | null>>([]);
  const [offset, setOffset] = useState(0);

  useEffect(() => {
    const calculateOffset = () => {
      if (containerRef.current) {
        const container = containerRef.current;
        const containerHeight = container.clientHeight;
        const activeElement = monthRefs.current[6];

        if (activeElement) {
          const activeElementTop = activeElement.offsetTop;
          const activeElementHeight = activeElement.clientHeight;
          const newOffset =
            containerHeight / 2 - (activeElementTop + activeElementHeight / 2);

          setOffset(newOffset);
        }
      }
    };

    const rafId = requestAnimationFrame(calculateOffset);

    return () => cancelAnimationFrame(rafId);
  }, [monthNumber]);

  return (
    <div
      ref={containerRef}
      className="flex flex-col gap-0 h-full overflow-hidden relative"
    >
      <motion.div
        className="flex flex-col gap-0"
        animate={{ y: offset }}
        transition={{ duration: 0.5, ease: "easeOut" }}
      >
        {cyclicMonths.map(({ name, number }, index) => {
          const isActive = number === monthNumber;

          return (
            <motion.div
              key={`${name}-${number}`}
              ref={el => {
                monthRefs.current[index] = el;
              }}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{
                duration: 0.5,
                delay: index * 0.1,
                ease: "easeOut",
              }}
            >
              <span
                className={classNames(
                  "block font-sans text-5xl md:text-[3.5rem] font-bold leading-none tracking-[-1.5px] transition-colors duration-300 ease-in-out",
                  isActive ? "text-white" : "text-[#B56B3A]",
                )}
              >
                {name}
              </span>
            </motion.div>
          );
        })}
      </motion.div>
    </div>
  );
};
